// === LAYOUT & NAVIGATION UTILITIES ===

/**
 * Display toast notification
 * @param {string} m - Message text
 * @param {string} c - CSS class (default 'a-b' = success)
 */
/**
 * Display toast notification
 * @param {string} m - Message text
 * @param {string} c - CSS class: 'a-b' success, 'a-r' error, 'a-g' accent, 'a-w' warning
 * @param {object} opts - Optional: { action: { label, fn }, duration, icon }
 */
/**
 * ⚠ A NOTIFICATION MUST NEVER ABORT ITS CALLER — 2026-08-17.
 *
 * `document.getElementById('toastC').appendChild(el)` threw when the container was absent, and
 * `toast` is called from the middle of DATA paths: `backfillSalaryCashTxs` announces a balance
 * correction, `reconcileAndRepair` announces a held-back repair. A throw there does not lose a
 * message, it abandons the write half-done. The container is static markup and has always been
 * there — which is exactly the kind of "always" that MOVE-005 was built on. The message still
 * reaches the console when the UI cannot take it.
 */
export function toast(m, c = 'a-b', opts = {}) {
  const host = typeof document !== 'undefined' && document.getElementById('toastC');
  if (!host) { console.warn('[toast] ' + m); return; }
  const el = document.createElement('div');
  el.className = 'toast ' + c;
  const icons = { 'a-b': '✓', 'a-r': '✗', 'a-g': 'ℹ', 'a-w': '⚠' };
  const icon = opts.icon || icons[c] || '✓';
  let html = `<span class="toast-icon">${icon}</span><span class="toast-msg">${m}</span>`;
  if (opts.action) {
    html += `<button class="toast-action">${opts.action.label}</button>`;
  }
  html += `<span class="toast-close">&times;</span>`;
  el.innerHTML = html;
  // Wire close button
  el.querySelector('.toast-close').addEventListener('click', () => el.remove());
  // Wire action button
  if (opts.action) {
    el.querySelector('.toast-action').addEventListener('click', () => {
      opts.action.fn();
      el.remove();
    });
  }
  host.appendChild(el);
  const dur = opts.duration || (opts.action ? 8000 : 5000);
  setTimeout(() => { if (el.parentNode) el.remove(); }, dur);
}

/**
 * Close a modal, and say so.
 *
 * A modal used to close in three unrelated places — this function, the Esc handler and the
 * backdrop click — each doing `classList.remove('active')` and telling nobody. Modules that
 * hold edits in DOM inputs and commit them on `change` (a blur event) therefore had no way
 * to know their inputs were about to disappear: Esc does not blur the focused field first.
 * That is one of the two ways three card payments entered on 2026-08-03 became one.
 *
 * One closer, one event. `zagx:modal-close` carries the modal id; a module that owns a modal
 * listens for its own id and flushes. Listeners are advisory — a throwing listener must not
 * stop the modal from closing. (audit 2026-08-04)
 *
 * @param {string} id - Modal element ID
 */
export function clMo(id) {
  _closeModalEl(document.getElementById(id));
}

/** The single place a modal loses `active`. Announce first, then close. */
function _closeModalEl(el) {
  if (!el) return;
  try {
    document.dispatchEvent(new CustomEvent('zagx:modal-close', { detail: { id: el.id } }));
  } catch (_e) { /* optional: never block a close */ }
  el.classList.remove('active');
}

/**
 * Unified modal behavior: Esc to close, backdrop click to close
 * Call once on DOMContentLoaded
 */
/**
 * Setup scroll fade indicators on table wrappers
 * Adds tw-fade class and monitors scroll position
 */
function _updateFade(el) {
  const atStart = el.scrollLeft <= 2;
  const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
  const overflows = el.scrollWidth > el.clientWidth + 4;
  if (!overflows) { el.classList.remove('tw-fade', 'scrolled-mid', 'scrolled-end'); return; }
  el.classList.add('tw-fade');
  el.classList.toggle('scrolled-end', atEnd);
  el.classList.toggle('scrolled-mid', !atStart);
}

/**
 * Scan for .tw elements, bind scroll listeners if new, update fade indicators.
 * Called explicitly after each render cycle (replaces body-wide MutationObserver).
 */
export function refreshScrollFades() {
  document.querySelectorAll('.tw').forEach(el => {
    if (!el._fadeInit) {
      el._fadeInit = true;
      el.addEventListener('scroll', () => _updateFade(el), { passive: true });
    }
    _updateFade(el);
  });
}

export function setupScrollFades() {
  // Initial scan
  refreshScrollFades();
  // Also check on window resize
  window.addEventListener('resize', () => {
    document.querySelectorAll('.tw').forEach(_updateFade);
  });
}

/**
 * Global "back to top" button — appears when the main content is scrolled down,
 * scrolls it back to top on click. App-wide. Call once on init.
 */
export function setupToTop() {
  const sc = document.querySelector('.main-content');
  if (!sc) return;
  let btn = document.getElementById('toTopBtn');
  if (!btn) {
    btn = document.createElement('button');
    btn.id = 'toTopBtn';
    btn.className = 'to-top-btn';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Scroll to top');
    btn.title = 'Back to top';
    btn.innerHTML = '&#8593;';
    btn.addEventListener('click', () => sc.scrollTo({ top: 0, behavior: 'smooth' }));
    document.body.appendChild(btn);
  }
  const onScroll = () => { btn.classList.toggle('show', sc.scrollTop > 500); };
  sc.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/**
 * Keyboard navigation shortcuts
 * 1-8: page switch, /: search focus, ?: show shortcuts help
 */
/**
 * Toggle mobile More overlay
 */
export function toggleMobileMore() {
  // With 6 nav items, all fit in mobile bottom bar — More is no longer needed
  // Kept as no-op for backward compatibility
}

export function setupKeyboard() {
  const pages = ['dashboard', 'portfolio', 'trades', 'cash', 'history'];
  document.addEventListener('keydown', (e) => {
    // Cmd+K / Ctrl+K: open global search (works even when typing in inputs)
    if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (window.openSearch) window.openSearch();
      return;
    }

    // Cmd+J / Ctrl+J: open AI chat
    if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (window.toggleAIChat) window.toggleAIChat();
      return;
    }

    // Skip if user is typing in an input/textarea/select
    const tag = e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
    // Skip if any modal is open (but not search overlay — search handles its own keys)
    if (document.querySelector('.mo.active')) return;

    // Number keys 1-5: navigate pages
    const num = parseInt(e.key);
    if (num >= 1 && num <= 5 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const page = pages[num - 1];
      const tab = document.querySelector(`.nav-tab[data-page="${page}"]`);
      if (tab) { tab.click(); e.preventDefault(); }
      return;
    }

    // / : open global search
    if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      if (window.openSearch) window.openSearch();
      return;
    }

    // ? : show keyboard shortcuts
    if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
      toast('Shortcuts: 1-5 pages · / or ⌘K search · ⌘J AI chat · ? help · Esc close', 'a-g', { duration: 4000 });
      e.preventDefault();
    }
  });
}

export function setupModals() {
  // Esc key closes topmost active modal
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const modals = document.querySelectorAll('.mo.active');
      if (modals.length) {
        _closeModalEl(modals[modals.length - 1]);
        e.preventDefault();
      }
    }
  });

  // Backdrop click: click on .mo background (not .mod content) closes it
  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('mo') && e.target.classList.contains('active')) {
      _closeModalEl(e.target);
    }
  });
}

// === MOBILE LAYOUT FIXER ===
// Most mobile styles now handled by CSS @media(max-width:768px)
// This JS function handles only inline-style grid overrides that CSS can't target

function _imp(el, prop, val) {
  el.style.setProperty(prop, val, 'important');
}

let _lastMobileState = null;

/**
 * Fix mobile layout — lightweight version
 * Most work now done by CSS media queries. JS only handles
 * inline-style grid-template-columns overrides.
 */
export function fixMobileLayout(force) {
  const isMobile = window.innerWidth <= 768;
  const newState = isMobile ? 'mobile' : 'desktop';
  if (!force && _lastMobileState === newState) return;
  _lastMobileState = newState;
  if (!isMobile) return;

  // Fix inline style grids that CSS can't override by selector alone
  const scope = document.querySelector('.page.active') || document;
  scope.querySelectorAll('[style*="grid-template-columns"]').forEach(el => {
    _imp(el, 'grid-template-columns', '1fr');
  });

  // FAB position (no tab bar on mobile now — top bar is at top)
  const fab = document.getElementById('qpFloat');
  if (fab) _imp(fab, 'bottom', '24px');
}

/**
 * Setup navigation tab click handlers
 * Switches active page and re-renders section if dirty
 * @param {Object} pageToSection - Map of page names to section keys
 * @param {Object} renderMap - Map of page names to render functions
 * @param {Object} dirty - Dirty state tracker
 */
const _pageNames = {
  dashboard: 'Dashboard', portfolio: 'Portfolio', monthly: 'Monthly Plan',
  trades: 'Trades', journal: 'Trade Journal', cash: 'Cash & P&L', cashflow: 'Income & Expenses',
  goals: 'Goals', history: 'History', settings: 'Settings',
  signals: 'Signals', bonus: 'Bonus', tax: 'Tax', integrity: 'Integrity'
};

// Group labels for top bar when group is active
const _groupNames = {
  dashboard: 'Dashboard', trades: 'Trades', cash: 'Money',
  monthly: 'Plan', history: 'History', settings: 'Settings'
};

// Sub-tab labels (shorter names for the tab pills)
const _subTabLabels = {
  dashboard: 'Overview', portfolio: 'Holdings',
  cash: 'Cash & P&L', cashflow: 'Income', bonus: 'Bonus',
  monthly: 'Monthly', goals: 'Goals', signals: 'Signals', tax: 'Tax', integrity: 'Integrity'
};

export function updateTopBar(page, groupPage) {
  const title = document.getElementById('tbPageTitle');
  const metric = document.getElementById('tbPageMetric');
  if (title) title.textContent = _groupNames[groupPage || page] || _pageNames[page] || page;
  if (metric) metric.textContent = '';// Metric updated by render functions per page
}

/**
 * Render sub-tab bar for a nav group
 * @param {string} groupPage - The default page of the group (matches data-page on nav-tab)
 * @param {string[]} subpages - Array of sub-page names
 * @param {string} activeSub - Currently active sub-page
 * @param {Function} onSwitch - Callback when sub-tab is clicked
 */
function _renderSubTabs(groupPage, subpages, activeSub, onSwitch) {
  const bar = document.getElementById('subTabBar');
  if (!bar) return;
  if (!subpages || subpages.length <= 1) {
    bar.style.display = 'none';
    return;
  }
  bar.style.display = 'flex';
  bar.innerHTML = subpages.map(pg => {
    const label = _subTabLabels[pg] || _pageNames[pg] || pg;
    const isActive = pg === activeSub;
    return `<button class="sub-tab${isActive ? ' active' : ''}" data-subtab="${pg}" role="tab" aria-selected="${isActive}">${label}</button>`;
  }).join('');

  bar.querySelectorAll('.sub-tab').forEach(btn => {
    btn.addEventListener('click', () => onSwitch(btn.dataset.subtab));
  });
}

export function setupNavigation(pageToSection, renderMap, dirty) {
  if (!pageToSection || !renderMap || !dirty) {
    console.warn('[ZAGX] setupNavigation called without args — dirty-flag rendering disabled');
  }
  const allTabs = document.querySelectorAll('.nav-tab');

  // Track which sub-page is active within each group
  const _groupActiveSub = {};

  // History: initial render replaces, user navigations push (so the browser Back button works).
  let _navHistoryReady = false;

  // per-section background identity (neon redesign): each page group gets its own bg color
  const _bgSection = { dashboard: 'dashboard', portfolio: 'portfolio', monthly: 'portfolio', trades: 'trades', journal: 'trades', signals: 'signals', risk: 'risk', cash: 'money', cashflow: 'money', bonus: 'money', goals: 'goals', history: 'tools', settings: 'tools', tax: 'tools', integrity: 'tools', advisory: 'advisory', ledger: 'advisory' };

  // Virtual subpages: reached via in-page buttons, not nav tabs/dropdowns.
  // The parent tab stays highlighted and NO sub-tab bar is rendered.
  const _virtualPages = { journal: 'trades', ledger: 'advisory' };

  const showPage = (page) => {
    document.querySelectorAll('.page').forEach(p => p.classList.remove('active'));
    const pageEl = document.getElementById('page-' + page);
    if (pageEl) pageEl.classList.add('active');
    document.body.setAttribute('data-section', _bgSection[page] || 'dashboard');

    if (!pageToSection || !renderMap || !dirty) return;
    const sec = pageToSection[page];
    if (page === 'settings') {
      if (renderMap.initSett) renderMap.initSett();
    } else if (sec && dirty[sec]) {
      if (renderMap[page]) renderMap[page]();
      dirty[sec] = false;
    }
    // Hash routing: reflect current page in the URL (refresh persistence + bookmarks).
    // Guard: never clobber a Supabase auth hash (#access_token / #error) and only for known pages.
    // Initial render replaces; subsequent user navigations push a history entry so the browser
    // Back/Forward buttons move between pages (the back-nav fix).
    if (page && _bgSection[page] !== undefined && !/access_token|error=/.test(location.hash) && location.hash.slice(1) !== page) {
      history[_navHistoryReady ? 'pushState' : 'replaceState'](null, '', '#' + page);
    }
  };

  const activateTab = (tab, subPage) => {
    const groupPage = tab.dataset.page;
    const subpagesStr = tab.dataset.subpages;
    const subpages = subpagesStr ? subpagesStr.split(',') : [groupPage];

    // Determine which sub-page to show
    let targetPage = subPage || _groupActiveSub[groupPage] || subpages[0];
    // Ensure targetPage is valid within the group
    if (!subpages.includes(targetPage)) targetPage = subpages[0];
    _groupActiveSub[groupPage] = targetPage;

    // Highlight active nav tab
    allTabs.forEach(t => {
      t.classList.remove('active');
      t.setAttribute('aria-selected', 'false');
    });
    tab.classList.add('active');
    tab.setAttribute('aria-selected', 'true');

    // Show the target page
    showPage(targetPage);
    updateTopBar(targetPage, groupPage);

    // Render sub-tabs with self-referencing callback
    const onSubSwitch = (newSub) => {
      _groupActiveSub[groupPage] = newSub;
      showPage(newSub);
      updateTopBar(newSub, groupPage);
      _renderSubTabs(groupPage, subpages, newSub, onSubSwitch);
      fixMobileLayout();
    };
    _renderSubTabs(groupPage, subpages, targetPage, onSubSwitch);

    fixMobileLayout();
  };

  // Expose activateTab for programmatic navigation (search, push notifications, etc.)
  window._navActivateTab = (page) => {
    // Virtual subpage: highlight the parent tab, show the page, hide sub-tabs.
    if (_virtualPages[page]) {
      const parent = _virtualPages[page];
      const parentTab = document.querySelector(`.nav-tab[data-page="${parent}"]`);
      if (parentTab) {
        allTabs.forEach(t => { t.classList.remove('active'); t.setAttribute('aria-selected', 'false'); });
        parentTab.classList.add('active');
        parentTab.setAttribute('aria-selected', 'true');
      }
      showPage(page);
      updateTopBar(page, null);
      _renderSubTabs(parent, [parent], parent, () => {});
      fixMobileLayout();
      return;
    }
    // Find which group contains this page
    for (const tab of allTabs) {
      const subpagesStr = tab.dataset.subpages;
      const subpages = subpagesStr ? subpagesStr.split(',') : [tab.dataset.page];
      if (subpages.includes(page)) {
        activateTab(tab, page);
        return;
      }
    }
    // Fallback: direct page match
    const directTab = document.querySelector(`.nav-tab[data-page="${page}"]`);
    if (directTab) activateTab(directTab, page);
  };

  allTabs.forEach(tab => {
    tab.addEventListener('click', () => activateTab(tab));
    // A11y: Enter/Space activates tab, Arrow keys navigate between tabs
    tab.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activateTab(tab);
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        const next = tab.nextElementSibling;
        if (next && next.classList.contains('nav-tab')) next.focus();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const prev = tab.previousElementSibling;
        if (prev && prev.classList.contains('nav-tab')) prev.focus();
      }
    });
  });

  // Wire dropdown menu items — clicking a dropdown sub-item navigates to that page
  document.querySelectorAll('.nav-dropdown a[data-nav-sub]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.stopPropagation();
      const targetPage = link.dataset.navSub;
      if (window._navActivateTab) window._navActivateTab(targetPage);
    });
  });

  // Initialize for the initially active tab (e.g. Dashboard on page load)
  const _initHash = (location.hash || '').replace(/^#/, '');
  const initialActive = document.querySelector('.nav-tab.active');
  if (initialActive) activateTab(initialActive);
  // Hash routing: honor a deep-link on load (only known pages — ignore Supabase auth hashes)
  if (_initHash && _bgSection[_initHash] !== undefined && window._navActivateTab) {
    window._navActivateTab(_initHash);
    // Force a render of the deep-linked page — its dirty flag may not be set yet during
    // init, so showPage()'s dirty-gate can skip it and leave the page blank.
    if (renderMap) {
      try {
        if (_initHash === 'settings' && renderMap.initSett) renderMap.initSett();
        else if (typeof renderMap[_initHash] === 'function') renderMap[_initHash]();
      } catch (_e) { /* render-on-deeplink best-effort */ }
    }
  }
  // Respond to hash changes (back/forward, manual edits, bookmarks)
  window.addEventListener('hashchange', () => {
    const h = (location.hash || '').replace(/^#/, '');
    if (h && _bgSection[h] !== undefined && window._navActivateTab) window._navActivateTab(h);
  });

  // Initial setup complete — from here, user navigations push history (Back button works).
  _navHistoryReady = true;
}
